import { useEffect, useRef, useState } from 'react';
import { isBrowser } from '@noths/polaris-client-utils';

import { isDateInPast } from './utils';

/**
 * Hook that returns the number of seconds remaining until a given end date/time
 * Updates every second and stops when the countdown reaches zero
 * @param endDateTime - ISO date string for the end date/time
 * @returns Number of seconds remaining, or 0 if date is in past/invalid or is Server Side Rendered (SSR)
 */
export const useCountdown = (endDateTime: string): number => {
  const [secondsRemaining, setSecondsRemaining] = useState<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endDateRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isBrowser() || isDateInPast(endDateTime)) {
      setSecondsRemaining(0);
      endDateRef.current = null;
      return;
    }

    // Parse end date once and store timestamp
    endDateRef.current = new Date(endDateTime).getTime();

    const updateCountdown = () => {
      if (endDateRef.current === null) {
        return;
      }

      const now = Date.now();
      const diff = Math.max(0, endDateRef.current - now);
      const seconds = Math.floor(diff / 1000);

      setSecondsRemaining(seconds);

      if (seconds === 0) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        endDateRef.current = null;
      }
    };

    updateCountdown();
    intervalRef.current = setInterval(updateCountdown, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      endDateRef.current = null;
    };
  }, [endDateTime]);

  return secondsRemaining;
};
